import { createElement, type ReactNode } from 'react';
import { type MaterialSymbol } from 'material-symbols';
import { Icon } from '@ics-portal/guardian';
import { IconEntityMap } from '@components/_atoms/icons';
import { type Maybe } from '@models/Maybe';

export const ProjectTypesMap = {
    Customer: 'PT01G6JHK1162M5N4JH0KGHY9Z04',
    ProductDevelopment: 'PT01G6JHNRC91127SVP0BMD75FSY',
    StandardSolution: 'PT01G6JJ8WTE07Y3FRNS5DJF88KX',
    Service: 'PT01G6N9MCZGC6QAYTHRTTJKBYV8',
    FactoryLifecycle: 'PT01G6NCP2KDSW4Q2B3160N0VSKQ',
} as const;

export type ProjectTypeKey =
    (typeof ProjectTypesMap)[keyof typeof ProjectTypesMap];

export interface ProjectTypePresentation {
    description?: string;
    icon: MaterialSymbol;
    iconColor?: string;
}

const PROJECT_TYPE_PRESENTATIONS: {
    id?: ProjectTypeKey;
    match?: string;
    presentation: ProjectTypePresentation;
}[] = [
    {
        id: ProjectTypesMap.Customer,
        match: 'Customer Project',
        presentation: {
            icon: IconEntityMap.ProjectTypeCustomer,
            iconColor: 'var(--guardianY90)',
            description:
                'Customer Project (PE@SE): Project following the PE@SE process (KB PSS in PE@SE/Engineering), usually a customer project with a defined delivery scope.',
        },
    },
    {
        id: ProjectTypesMap.ProductDevelopment,
        match: 'Product Development',
        presentation: {
            icon: IconEntityMap.ProjectTypeProductDevelopment,
            iconColor: 'var(--guardianE90)',
            description:
                'Product Development (PLM): Project managed through the PLM process, covering product lifecycle from concept to delivery.',
        },
    },
    {
        id: ProjectTypesMap.StandardSolution,
        presentation: {
            icon: IconEntityMap.ProjectTypeStandardSolution,
        },
    },
    {
        id: ProjectTypesMap.Service,
        presentation: {
            icon: IconEntityMap.ProjectTypeService,
        },
    },
    {
        id: ProjectTypesMap.FactoryLifecycle,
        match: 'Factory Lifecycle',
        presentation: {
            icon: IconEntityMap.ProjectTypeFactoryLifecycle,
            iconColor: 'var(--guardianB40)',
            description:
                'Factory Lifecycle Management: Project focused on managing and optimizing the factory lifecycle, including production assets and processes.',
        },
    },
];

export function getProjectTypePresentation(params: {
    label?: Maybe<string>;
    typeId?: Maybe<string>;
}): Maybe<ProjectTypePresentation> {
    const { typeId, label } = params;

    const byId = PROJECT_TYPE_PRESENTATIONS.find(
        (entry) => entry.id === typeId,
    );
    if (byId) {
        return byId.presentation;
    }

    const byLabel = PROJECT_TYPE_PRESENTATIONS.find((entry) =>
        entry.match
            ? label?.toLowerCase().includes(entry.match.toLowerCase())
            : false,
    );

    return byLabel?.presentation;
}

export function getProjectIcon(id: Maybe<string>): ReactNode {
    const presentation = getProjectTypePresentation({ typeId: id });

    return createElement(Icon, {
        name: presentation?.icon ?? IconEntityMap.ProjectTypeDefault,
        style: { color: presentation?.iconColor },
    });
}
